import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BOOKING_API = "https://bmtvendorvendorfinal.onrender.com/api/homestay-bookings";
const INVENTORY_API = "https://bmtvendorvendorfinal.onrender.com/api/homestay-inventory";
const HOMESTAY_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/homestay";

const getToken = () => {
  const t = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  return t && t !== "null" && t !== "undefined" ? t : null;
};
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

/* ---------- date helpers ---------- */
const pad = (n) => String(n).padStart(2, "0");
const localKey = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`; };
const utcKey = (d) => new Date(d).toISOString().slice(0, 10); // stay dates are stored at UTC midnight
const addDays = (key, n) => new Date(Date.parse(key) + n * 864e5).toISOString().slice(0, 10);
const diffDays = (a, b) => Math.round((Date.parse(a) - Date.parse(b)) / 864e5);
const fmtShort = (key) => new Date(key).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
const fmtWeek = (key) => new Date(key).toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
const money = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

const EARNING = ["CONFIRMED", "CHECKED_IN", "CHECKED_OUT", "COMPLETED"];
const STATUS_LABEL = {
  PENDING: ["Pending", "#b45309", "#fef3c7"], CONFIRMED: ["Confirmed", "#166534", "#dcfce7"],
  CHECKED_IN: ["Checked in", "#1d4ed8", "#dbeafe"], CHECKED_OUT: ["Checked out", "#0e7490", "#cffafe"],
  COMPLETED: ["Completed", "#4b5563", "#f3f4f6"], CANCELLED: ["Cancelled", "#b91c1c", "#fee2e2"],
  REJECTED: ["Rejected", "#b91c1c", "#fee2e2"], NO_SHOW: ["No show", "#374151", "#e5e7eb"],
};

export default function HomestayDashboard({ onNavigate }) {
  const [data, setData] = useState({ bookings: [], homestays: [], inventory: [] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const today = localKey(new Date());

  const load = useCallback(async () => {
    if (!getToken()) { window.location.href = "/vendor-login"; return; }
    setLoading(true);
    const h = authHeaders();
    const [b, hs, inv] = await Promise.allSettled([
      axios.get(`${BOOKING_API}/vendor`, { ...h, params: { limit: 300 } }),
      axios.get(`${HOMESTAY_API}/my?limit=100`, h),
      axios.get(INVENTORY_API, { ...h, params: { startDate: today, endDate: addDays(today, 59), limit: 1000 } }),
    ]);
    const bad = [];
    if (b.status === "rejected") bad.push("bookings");
    if (hs.status === "rejected") bad.push("properties");
    if (inv.status === "rejected") bad.push("inventory");
    setFailed(bad);
    setData({
      bookings: b.status === "fulfilled" ? b.value.data.bookings || [] : [],
      homestays: hs.status === "fulfilled" ? hs.value.data.homestays || [] : [],
      inventory: inv.status === "fulfilled" ? inv.value.data.inventory || [] : [],
    });
    setUpdatedAt(new Date());
    setLoading(false);
  }, [today]);

  useEffect(() => { load(); }, [load]);

  /* ---------- numbers ---------- */
  const m = useMemo(() => {
    const { bookings, homestays, inventory } = data;
    const monthPrefix = today.slice(0, 7);
    const weekStart = addDays(today, -6);

    const earning = bookings.filter((b) => EARNING.includes(b.bookingStatus));
    const sum = (arr) => arr.reduce((s, b) => s + Number(b.totalAmount || 0), 0);
    const bookedOn = (b) => (b.createdAt ? localKey(b.createdAt) : "");

    const revToday = earning.filter((b) => bookedOn(b) === today);
    const rev7 = earning.filter((b) => bookedOn(b) >= weekStart && bookedOn(b) <= today);
    const revMonth = earning.filter((b) => bookedOn(b).startsWith(monthPrefix));
    const nights7 = rev7.reduce((s, b) => s + Number(b.nights || 0) * Number(b.unitsBooked || 1), 0);

    const stays = bookings.filter((b) => ["CONFIRMED", "CHECKED_IN"].includes(b.bookingStatus));
    const tonight = stays.filter((b) => utcKey(b.checkInDate) <= today && utcKey(b.checkOutDate) > today);

    const pending = bookings.filter((b) => b.bookingStatus === "PENDING");
    const arrivals = bookings.filter((b) => b.bookingStatus === "CONFIRMED" && utcKey(b.checkInDate) === today);
    const inHouse = bookings.filter((b) => b.bookingStatus === "CHECKED_IN");
    const departures = inHouse.filter((b) => utcKey(b.checkOutDate) <= today);

    const upcoming = bookings
      .filter((b) => ["PENDING", "CONFIRMED"].includes(b.bookingStatus) && utcKey(b.checkInDate) >= today)
      .sort((a, b) => new Date(a.checkInDate) - new Date(b.checkInDate))
      .slice(0, 6);

    const recent = [...bookings]
      .sort((a, b) => new Date(b.createdAt || b.checkInDate) - new Date(a.createdAt || a.checkInDate))
      .slice(0, 5);

    // availability for the next 7 nights, summed across rooms
    const week = Array.from({ length: 7 }, (_, i) => {
      const key = addDays(today, i);
      const rows = inventory.filter((r) => utcKey(r.date) === key);
      const open = rows.filter((r) => !r.isBlocked && !r.stopSell && !r.closedForBooking);
      const total = rows.reduce((s, r) => s + Number(r.totalUnits || 0), 0);
      const booked = rows.reduce((s, r) => s + Number(r.bookedUnits || 0), 0);
      const available = open.reduce((s, r) => s + Number(r.availableUnits || 0), 0);
      return { key, rows: rows.length, openRows: open.length, total, booked, available };
    });

    const openDays30 = new Set(
      inventory.filter((r) => utcKey(r.date) < addDays(today, 30) && !r.isBlocked && !r.stopSell && !r.closedForBooking).map((r) => utcKey(r.date))
    ).size;

    const hasApproved = homestays.some((x) => x.status === "APPROVED");
    const hasRooms = homestays.some((x) => x.roomCount > 0);
    const hasPhotos = homestays.length > 0 && homestays.every((x) => x.coverImage || x.propertyImages?.length);
    const checklist = [
      { id: "p", done: homestays.length > 0, title: "Add your property", hint: "Name, address and host details", go: "homestays_manage" },
      { id: "ph", done: hasPhotos, title: "Upload photos", hint: "Guests book properties with clear photos", go: "homestays_manage" },
      { id: "r", done: hasRooms, title: "Add rooms and base rates", hint: "Each room type needs a price", go: "homestay_units" },
      { id: "a", done: hasApproved, title: "Get approved", hint: homestays.some((x) => x.status === "PENDING") ? "Under review by BirdMyTrip" : "Submit your property for approval", go: "homestays_manage" },
      { id: "i", done: openDays30 >= 7, title: "Open dates for sale", hint: openDays30 ? `${openDays30} of next 30 days open` : "Guests can't book until dates are open", go: "homestay_inventory" },
    ];

    return {
      revToday: sum(revToday), rev7: sum(rev7), revMonth: sum(revMonth),
      adr7: nights7 ? sum(rev7) / nights7 : 0, nights7,
      tonight: tonight.reduce((s, b) => s + Number(b.unitsBooked || 1), 0),
      pending, arrivals, inHouse, departures, upcoming, recent, week, checklist,
      totalRooms: homestays.reduce((s, x) => s + Number(x.roomCount || 0), 0),
      homestayCount: homestays.length,
    };
  }, [data, today]);

  const done = m.checklist.filter((c) => c.done).length;
  const nextStep = m.checklist.find((c) => !c.done);

  /* ============================================================ RENDER */
  return (
    <div style={S.page} className="hd-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes hdspin { to { transform: rotate(360deg); } }
        .hd-page button:focus-visible { outline: 2px solid #166534; outline-offset: 2px; }
        .hd-row:hover { background: #f8fafc; }
        .hd-kpis { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 16px; }
        .hd-cols { display: grid; grid-template-columns: minmax(0,1.35fr) minmax(0,1fr); gap: 20px; align-items: start; }
        @media (max-width: 1000px) { .hd-kpis { grid-template-columns: repeat(2, minmax(0,1fr)); } .hd-cols { grid-template-columns: 1fr; } }
        @media (max-width: 520px) { .hd-kpis { grid-template-columns: 1fr; } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; } }
      `}</style>

      <div style={S.header}>
        <div>
          <h1 style={S.title}>{greeting()}</h1>
          <p style={S.subtitle}>
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
            {updatedAt && `. Updated ${updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button style={S.ghost} onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
          <button style={S.primary} onClick={() => onNavigate("homestay_inventory")}>Open dates and rates</button>
        </div>
      </div>

      {failed.length > 0 && (
        <div style={S.warn}>Could not load {failed.join(", ")}. Numbers below may be incomplete. <button style={S.link} onClick={load}>Try again</button></div>
      )}

      {loading && !updatedAt ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : (
        <>
          {/* attention banner */}
          {m.pending.length > 0 ? (
            <div style={S.banner}>
              <div>
                <div style={S.bannerTitle}>{m.pending.length} booking request{m.pending.length > 1 ? "s" : ""} waiting for you</div>
                <div style={S.bannerSub}>Guests are waiting for a reply. Accept or reject so the dates are not held for long.</div>
              </div>
              <button style={S.bannerBtn} onClick={() => onNavigate("homestays_bookings")}>Review requests</button>
            </div>
          ) : nextStep && done < m.checklist.length ? (
            <div style={{ ...S.banner, background: "#f0fdf4", borderColor: "#bbf7d0" }}>
              <div>
                <div style={{ ...S.bannerTitle, color: "#166534" }}>Next step: {nextStep.title}</div>
                <div style={{ ...S.bannerSub, color: "#4b7a5a" }}>{nextStep.hint}</div>
              </div>
              <button style={{ ...S.bannerBtn, background: "#166534" }} onClick={() => onNavigate(nextStep.go)}>Go there</button>
            </div>
          ) : null}

          {/* today */}
          <div className="hd-kpis" style={{ marginBottom: 16 }}>
            <Stat label="Arriving today" value={m.arrivals.length} note={m.arrivals[0] ? m.arrivals.map((b) => b.guestName).slice(0, 2).join(", ") : "No arrivals"} onClick={() => onNavigate("homestays_bookings")} />
            <Stat label="In house now" value={m.inHouse.length} note={`${m.tonight} room${m.tonight === 1 ? "" : "s"} occupied tonight`} onClick={() => onNavigate("homestays_bookings")} />
            <Stat label="Leaving today" value={m.departures.length} note={m.departures.length ? "Check them out when they leave" : "No departures"} onClick={() => onNavigate("homestays_bookings")} />
            <Stat label="Pending requests" value={m.pending.length} hot={m.pending.length > 0} note={m.pending.length ? "Needs your reply" : "All caught up"} onClick={() => onNavigate("homestays_bookings")} />
          </div>

          {/* money */}
          <section style={{ ...S.card, marginBottom: 20 }}>
            <div style={S.cardHead}>
              <h2 style={S.h2}>Revenue</h2>
              <span style={S.muted}>Counted on the day the booking was made</span>
            </div>
            <div className="hd-kpis">
              <Money label="Today" value={money(m.revToday)} />
              <Money label="Last 7 days" value={money(m.rev7)} />
              <Money label="This month" value={money(m.revMonth)} />
              <Money label="Average rate per night, 7 days" value={m.nights7 ? money(m.adr7) : "—"} note={m.nights7 ? `${m.nights7} room nights sold` : "No sales yet"} />
            </div>
          </section>

          <div className="hd-cols">
            <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
              {/* availability */}
              <section style={S.card}>
                <div style={S.cardHead}>
                  <h2 style={S.h2}>Next 7 nights</h2>
                  <button style={S.link} onClick={() => onNavigate("homestay_inventory")}>Manage inventory</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {m.week.map((d) => <DayBar key={d.key} d={d} isToday={d.key === today} />)}
                </div>
                {m.week.every((d) => d.rows === 0) && (
                  <p style={{ ...S.muted, marginTop: 14 }}>No dates are open yet. Add inventory so guests can book.</p>
                )}
              </section>

              {/* upcoming */}
              <section style={S.card}>
                <div style={S.cardHead}>
                  <h2 style={S.h2}>Upcoming arrivals</h2>
                  <button style={S.link} onClick={() => onNavigate("homestays_bookings")}>All bookings</button>
                </div>
                {m.upcoming.length === 0 ? (
                  <Empty text="No upcoming arrivals" />
                ) : (
                  m.upcoming.map((b) => {
                    const dd = diffDays(utcKey(b.checkInDate), today);
                    const [lab, c, bg] = STATUS_LABEL[b.bookingStatus] || STATUS_LABEL.COMPLETED;
                    return (
                      <div key={b._id} className="hd-row" style={S.item}>
                        <div style={S.dateChip}>
                          <b style={{ fontSize: 18, fontFamily: "'Playfair Display', serif" }}>{fmtShort(utcKey(b.checkInDate)).split(" ")[0]}</b>
                          <span style={{ fontSize: 11, color: "#6b7280" }}>{fmtShort(utcKey(b.checkInDate)).split(" ")[1]}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.itemTitle}>{b.guestName}</div>
                          <div style={S.muted}>{b.room?.roomName || "Room"}, {b.nights} night{b.nights > 1 ? "s" : ""}, {b.unitsBooked} room{b.unitsBooked > 1 ? "s" : ""}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ ...S.badge, color: c, background: bg }}>{lab}</span>
                          <div style={{ ...S.muted, marginTop: 4 }}>{dd === 0 ? "Today" : dd === 1 ? "Tomorrow" : `In ${dd} days`}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </section>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
              {/* checklist */}
              <section style={S.card}>
                <div style={S.cardHead}>
                  <h2 style={S.h2}>Setup progress</h2>
                  <span style={{ ...S.muted, fontWeight: 600 }}>{done} of {m.checklist.length}</span>
                </div>
                <div style={S.progress}><div style={{ ...S.progressFill, width: `${(done / m.checklist.length) * 100}%` }} /></div>
                <div style={{ marginTop: 8 }}>
                  {m.checklist.map((c) => (
                    <button key={c.id} onClick={() => onNavigate(c.go)} style={S.check}>
                      <span style={{ ...S.tick, ...(c.done ? S.tickOn : {}) }}>{c.done ? "✓" : ""}</span>
                      <span style={{ flex: 1, textAlign: "left" }}>
                        <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: c.done ? "#9ca3af" : "#111827", textDecoration: c.done ? "line-through" : "none" }}>{c.title}</span>
                        {!c.done && <span style={S.muted}>{c.hint}</span>}
                      </span>
                      {!c.done && <span style={{ color: "#166534", fontWeight: 700 }}>›</span>}
                    </button>
                  ))}
                </div>
              </section>

              {/* recent */}
              <section style={S.card}>
                <div style={S.cardHead}>
                  <h2 style={S.h2}>Latest bookings</h2>
                </div>
                {m.recent.length === 0 ? (
                  <Empty text="No bookings yet" />
                ) : (
                  m.recent.map((b) => {
                    const [lab, c, bg] = STATUS_LABEL[b.bookingStatus] || STATUS_LABEL.COMPLETED;
                    return (
                      <div key={b._id} className="hd-row" style={S.item}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.itemTitle}>{b.guestName}</div>
                          <div style={S.muted}>{fmtShort(utcKey(b.checkInDate))} to {fmtShort(utcKey(b.checkOutDate))}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 700, color: "#166534", fontSize: 14 }}>{money(b.totalAmount)}</div>
                          <span style={{ ...S.badge, color: c, background: bg }}>{lab}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </section>

              <section style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{m.homestayCount} propert{m.homestayCount === 1 ? "y" : "ies"}, {m.totalRooms} room type{m.totalRooms === 1 ? "" : "s"}</div>
                  <div style={S.muted}>Edit details, photos and rooms</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.ghost} onClick={() => onNavigate("homestays_manage")}>Property</button>
                  <button style={S.ghost} onClick={() => onNavigate("homestay_units")}>Rooms</button>
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- small pieces ---------- */
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const Stat = ({ label, value, note, hot, onClick }) => (
  <button onClick={onClick} style={{ ...S.stat, ...(hot ? S.statHot : {}) }}>
    <span style={S.statLabel}>{label}</span>
    <span style={{ ...S.statValue, color: hot ? "#b45309" : "#111827" }}>{value}</span>
    <span style={S.muted}>{note}</span>
  </button>
);

const Money = ({ label, value, note }) => (
  <div>
    <div style={S.statLabel}>{label}</div>
    <div style={{ ...S.statValue, color: "#166534", fontSize: 26 }}>{value}</div>
    {note && <div style={S.muted}>{note}</div>}
  </div>
);

const Empty = ({ text }) => <p style={{ ...S.muted, padding: "18px 0", textAlign: "center", fontSize: 14 }}>{text}</p>;

function DayBar({ d, isToday }) {
  const label = `${isToday ? "Today" : fmtWeek(d.key)}, ${fmtShort(d.key)}`;
  let body;
  if (d.rows === 0) {
    body = <span style={{ ...S.muted, fontStyle: "italic" }}>Not open for sale</span>;
  } else if (d.openRows === 0) {
    body = <span style={{ fontSize: 13, color: "#b91c1c", fontWeight: 600 }}>Closed or blocked</span>;
  } else {
    const pct = d.total ? Math.min(100, (d.booked / d.total) * 100) : 0;
    body = (
      <>
        <div style={S.track}><div style={{ ...S.fill, width: `${pct}%`, background: pct >= 100 ? "#6b7280" : "#166534" }} /></div>
        <span style={{ fontSize: 12.5, color: "#374151", whiteSpace: "nowrap", minWidth: 96, textAlign: "right" }}>
          {d.booked}/{d.total} sold{d.available === 0 ? ", full" : ""}
        </span>
      </>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ width: 96, fontSize: 13, fontWeight: isToday ? 700 : 500, color: "#374151", flexShrink: 0 }}>{label}</span>
      {body}
    </div>
  );
}

/* ---------- styles ---------- */
const S = {
  page: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px 48px", fontFamily: "'DM Sans', sans-serif", background: "#f8fafc", minHeight: "100vh", color: "#111827" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: "#6b7280", marginTop: 6 },
  primary: { padding: "11px 20px", background: "#166534", color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  ghost: { padding: "9px 16px", background: "white", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit" },
  link: { background: "none", border: "none", color: "#166534", fontSize: 13.5, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" },
  warn: { background: "#fef3c7", color: "#92400e", padding: "10px 16px", borderRadius: 10, fontSize: 14, marginBottom: 16 },
  banner: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 14, padding: "16px 20px", marginBottom: 20 },
  bannerTitle: { fontSize: 16, fontWeight: 700, color: "#92400e" },
  bannerSub: { fontSize: 13.5, color: "#a16207", marginTop: 2 },
  bannerBtn: { padding: "10px 20px", background: "#d97706", color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

  stat: { display: "flex", flexDirection: "column", gap: 4, textAlign: "left", background: "white", border: "1px solid #eef0f3", borderRadius: 14, padding: "16px 18px", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 1px 4px rgba(0,0,0,.05)" },
  statHot: { background: "#fffbeb", borderColor: "#fde68a" },
  statLabel: { fontSize: 13, color: "#6b7280", fontWeight: 600 },
  statValue: { fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 700, lineHeight: 1.1 },
  muted: { fontSize: 12.5, color: "#9ca3af" },

  card: { background: "white", borderRadius: 16, padding: "20px 22px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 16 },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 700, margin: 0 },

  item: { display: "flex", alignItems: "center", gap: 14, padding: "12px 8px", borderTop: "1px solid #f3f4f6", borderRadius: 8 },
  itemTitle: { fontSize: 14.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  dateChip: { width: 46, display: "flex", flexDirection: "column", alignItems: "center", background: "#f8fafc", border: "1px solid #eef0f3", borderRadius: 10, padding: "5px 0", flexShrink: 0 },
  badge: { fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, display: "inline-block" },

  track: { flex: 1, height: 10, background: "#eef2f0", borderRadius: 6, overflow: "hidden" },
  fill: { height: "100%", borderRadius: 6 },

  progress: { height: 8, background: "#eef2f0", borderRadius: 6, overflow: "hidden" },
  progressFill: { height: "100%", background: "#166534", borderRadius: 6, transition: "width .3s" },
  check: { display: "flex", alignItems: "center", gap: 12, width: "100%", background: "none", border: "none", borderTop: "1px solid #f3f4f6", padding: "12px 4px", cursor: "pointer", fontFamily: "inherit" },
  tick: { width: 22, height: 22, borderRadius: "50%", border: "2px solid #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", flexShrink: 0 },
  tickOn: { background: "#166534", borderColor: "#166534" },

  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 260 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "hdspin 1s linear infinite" },
};